import * as yup from "yup";
import { basicPrimaryActivityKeys, basicPrimaryActivityPreKeys, constructionPrimaryBusinessActivityDescriptionOptionsSpecifyReq, healthCarePrimaryBusinessActivityDescriptionOptionsSpecifyReq, retailPrimaryBusinessActivitySpecifyRef } from "./EinTaxValueConstants";

export const einTaxIdValidationSchema = (validateTerms: boolean = true) => yup.object().shape({
    legalStructure: yup.string().required("Required"),
    partnershipType: yup.string().when("legalStructure", { is: "Partnerships", then: yup.string().required("Required"), }),
    membersInLLC: yup.number().min(1, "Must be at least 1").required("Required"),

    // if legalStructure is LLC and membersInLLC is 2 then llcMembersHusbandWife is required
    llcMembersHusbandWife: yup.string().when(["legalStructure", "membersInLLC"], {
        is: (legalStructure: string, membersInLLC: number) => {
            return legalStructure === "LLC" && membersInLLC === 2;
        },
        then: yup.string().required("Required"),
    }),
    // if legalStructure is LLC then and membersInLLC is 2 and llcMembersHusbandWife is yes then llcClassification is required
    llcClassification: yup.string().when(["legalStructure", "membersInLLC", "llcMembersHusbandWife"], {
        is: (legalStructure: string, membersInLLC: number, llcMembersHusbandWife: string) => {
            return legalStructure === "LLC" && membersInLLC === 2 && llcMembersHusbandWife === "yes";
        },
        then: yup.string().required("Required"),
    }),

    whyRequesting: yup.string().required("Required"),
    responsibleParty: yup.object().shape({
        firstName: yup.string().required("Required"),
        middleName: yup.string(),
        lastName: yup.string().required("Required"),
        suffix: yup.string(),
        ssnItin: yup.string().required("Please enter a valid ssn or itin(9 digits)").length(11, "Must be 9 digits").matches(/[0-9-]{11}/g, "Must be only digits"),
        ssnOrItin: yup.string().required("Required"),
    }),
    physicalLocation: yup.object().shape({
        address: yup.string().required("Required"),
        city: yup.string().required("Required"),
        state: yup.string().required("Required"),
        zip: yup.string().required("Required"),
    }),
    differentAddressInfo: yup.object().shape({
        differentAddress: yup.string().required("Required"),
        address: yup.string().when("differentAddress", { is: "yes", then: yup.string().required("Required"), }),
        city: yup.string().when("differentAddress", { is: "yes", then: yup.string().required("Required"), }),
        state: yup.string().when("differentAddress", { is: "yes", then: yup.string().required("Required"), }),
        zip: yup.string().when("differentAddress", { is: "yes", then: yup.string().required("Required"), }),
    }),
    // continue EIN stuff
    about: yup.object().shape({
        legalName: yup.string().required("Required"),
        tradeNameDBA: yup.string(),
        county: yup.string().required("Required"),
        stateArticlesFiled: yup.string().required("Required"),
        fileDate: yup.date().required("Required"),
        closingMonthAccountingPeriod: yup.string().required("Required"),
        highwayVehicle: yup.string().required("Required"),
        gamblingOrWagering: yup.string().required("Required"),
        form720: yup.string().required("Required"),
        alcoholTobaccoFirearms: yup.string().required("Required"),
        w2Info: yup.object().shape({
            expectW2: yup.string().required("Required"),
            firstDateWages: yup.date().when("expectW2", { is: "yes", then: yup.date().required("Required"), }),
            highestAgricultureEmployeeCount: yup.number().when("expectW2", { is: "yes", then: yup.number().min(1, "Must be at least 1").required("Required"), }),
            highestEmployeeCount: yup.number().when("expectW2", { is: "yes", then: yup.number().min(1, "Must be at least 1").required("Required"), }),
            taxLiableLessThan1k: yup.string().when("expectW2", { is: "yes", then: yup.string().required("Required"), }),
        }),
    }),

    primaryBusinessActivity: yup.string().required("Required"),

    // ACTIVITY PRE DESCRIPTION
    primaryBusinessActivityPreDescription: yup.string().when(["primaryBusinessActivity"], {
        is: (primaryBusinessActivity: string) => {
            if (basicPrimaryActivityPreKeys[primaryBusinessActivity]) {
                return true;
            }
            return false;
        },
        then: yup.string().required("Required"),
    }),

    // ACTIVITY DESCRIPTION
    primaryBusinessActivityDescription: yup.string().when(
        [
            "primaryBusinessActivity",
            "primaryBusinessActivityPreDescription",
        ],
        {
            is: (
                primaryBusinessActivity: string,
                primaryBusinessActivityPreDescription: string
            ) => {
                if (basicPrimaryActivityKeys[primaryBusinessActivity]) {
                    return true;
                }
                if (
                    primaryBusinessActivity === "Construction" &&
                    primaryBusinessActivityPreDescription === "no"
                ) {
                    return true;
                }
                if (
                    primaryBusinessActivity === "Health Care" &&
                    primaryBusinessActivityPreDescription !== ""
                ) {
                    return true;
                }
                if (
                    primaryBusinessActivity === "Real Estate" &&
                    primaryBusinessActivityPreDescription !== "" &&
                    primaryBusinessActivityPreDescription !==
                    "I sell property for others" &&
                    primaryBusinessActivityPreDescription !==
                    "I manage real estate for others" &&
                    primaryBusinessActivityPreDescription !== "Other"
                ) {
                    return true;
                }
                if (
                    primaryBusinessActivity === "Transportation" &&
                    (primaryBusinessActivityPreDescription === "Cargo" ||
                        primaryBusinessActivityPreDescription === "Passengers")
                ) {
                    return true;
                }
                return false;
            },
            then: yup.string().required("Required"),
        }
    ),

    // SPECIFY TYPE
    primaryBusinessActivitySpecifyType: yup.string().when(
        [
            "primaryBusinessActivity",
            "primaryBusinessActivityPreDescription",
            "primaryBusinessActivityDescription",
        ],
        {
            is: (
                primaryBusinessActivity: string,
                primaryBusinessActivityPreDescription: string,
                primaryBusinessActivityDescription: string
            ) => {
                if (
                    primaryBusinessActivity === "Real Estate" &&
                    primaryBusinessActivityPreDescription ===
                    "I use capital to build property" &&
                    primaryBusinessActivityDescription === "no"
                ) {
                    return true;
                }

                if (
                    (primaryBusinessActivity === "Wholesale" &&
                        primaryBusinessActivityDescription === "no")
                ) {
                    return true;
                }


                return false;
            },
            then: yup.string().required("Required"),
        }
    ),


    // SPECIFY
    primaryBusinessActivitySpecify: yup.string().when(
        [
            "primaryBusinessActivity",
            "primaryBusinessActivityPreDescription",
            "primaryBusinessActivityDescription",
            "primaryBusinessActivitySpecifyType",
        ],
        {
            is: (
                primaryBusinessActivity: string,
                primaryBusinessActivityPreDescription: string,
                primaryBusinessActivityDescription: string,
                primaryBusinessActivitySpecifyType: string
            ) => {
                if (
                    primaryBusinessActivity === "Other" ||
                    primaryBusinessActivity === "Manufacturing"
                ) {
                    return true;
                }
                if (
                    basicPrimaryActivityKeys[primaryBusinessActivity] &&
                    primaryBusinessActivityDescription === "Other"
                ) {
                    return true;
                }
                if (
                    primaryBusinessActivity === "Construction" &&
                    (primaryBusinessActivityPreDescription === "yes" ||
                        (primaryBusinessActivityPreDescription === "no" &&
                            constructionPrimaryBusinessActivityDescriptionOptionsSpecifyReq[
                            primaryBusinessActivityDescription
                            ]))
                ) {
                    return true;
                }
                if (
                    primaryBusinessActivity === "Health Care" &&
                    primaryBusinessActivityPreDescription !== "" &&
                    healthCarePrimaryBusinessActivityDescriptionOptionsSpecifyReq[
                    primaryBusinessActivityDescription
                    ]
                ) {
                    return true;
                }
                if (
                    primaryBusinessActivity === "Real Estate" &&
                    ((primaryBusinessActivityPreDescription ===
                        "I rent or lease property that I own" &&
                        primaryBusinessActivityDescription === "Other") ||
                        (primaryBusinessActivityPreDescription ===
                            "I use capital to build property" &&
                            primaryBusinessActivityDescription === "yes") ||
                        (primaryBusinessActivityPreDescription ===
                            "I use capital to build property" &&
                            primaryBusinessActivityDescription === "no" &&
                            constructionPrimaryBusinessActivityDescriptionOptionsSpecifyReq[
                            primaryBusinessActivitySpecifyType
                            ]) ||
                        primaryBusinessActivityPreDescription === "Other")
                ) {
                    return true;
                }
                if (
                    primaryBusinessActivity === "Rental & Leasing" &&
                    (primaryBusinessActivityDescription ===
                        "I rent or lease goods" ||
                        primaryBusinessActivityDescription ===
                        "I rent, lease, or sell real estate")
                ) {
                    return true;
                }
                if (
                    primaryBusinessActivity === "Retail" &&
                    retailPrimaryBusinessActivitySpecifyRef[
                    primaryBusinessActivityDescription
                    ]
                ) {
                    return true;
                }
                if (
                    primaryBusinessActivity === "Transportation" &&
                    (primaryBusinessActivityDescription === "Other" ||
                        primaryBusinessActivityPreDescription ===
                        "I provide a support activity for transportation")
                ) {
                    return true;
                }
                if (
                    (primaryBusinessActivity === "Wholesale" &&
                        primaryBusinessActivityDescription === "yes") ||
                    (primaryBusinessActivityDescription === "no" &&
                        primaryBusinessActivitySpecifyType === "no")
                ) {
                    return true;
                }
                return false;
            },
            then: yup.string().required("Required"),
        }
    ),
    ...(validateTerms ? {
        digitalSignature: yup.string().required("Required"),
    } : {}),
});

export const legalNameValidationSchema = yup.object().shape({
    about: yup.object().shape({ tradeNameDBA: yup.string().required("Required"), }),
    physicalLocation: yup.object().shape({
        address: yup.string().required("Required"),
        city: yup.string().required("Required"),
        state: yup.string().required("Required"),
        zip: yup.string().required("Required"),
    }),
    differentAddressInfo: yup.object().shape({
        differentAddress: yup.string().required("Required"),
        address: yup.string().when("differentAddress", { is: "yes", then: yup.string().required("Required"), }),
        city: yup.string().when("differentAddress", { is: "yes", then: yup.string().required("Required"), }),
        state: yup.string().when("differentAddress", { is: "yes", then: yup.string().required("Required"), }),
        zip: yup.string().when("differentAddress", { is: "yes", then: yup.string().required("Required"), }),
    }),
    digitalSignature: yup.string().required("Required"),
})

export const fictNameRenewalValidationSchema = yup.object().shape({
    tradeNameDBA: yup.string().required('Required'),
    isThisYourInformation: yup.string().required('Required'),
    informationStillAccurate: yup.string().required('Required'),

    updateAddEin: yup.string().when("informationStillAccurate", { is: "no", then: yup.string().required("Required"), }),
    feiEin: yup.string().when("updateAddEin", {
        is: "yes",
        then: yup.string().required("Please enter a valid FEI/EIN (9 digits)").length(10, "Must be 9 digits").matches(/[0-9-]{10}/g, "Must be only digits"),
    }),
    changeOwner: yup.string().when("informationStillAccurate", {
        is: "no",
        then: yup.string().required("Required"),
    }),

    deletingOwners: yup.string().when("changeOwner", { is: "yes", then: yup.string().required("Required"), }),
    deletedOwners: yup.array().when(["changeOwner", "deletingOwners"], {
        is: (a: string, b: string) => {
            return a === "yes" && b === "yes";
        },
        then: yup.array().of(
            yup.object().shape({
                name: yup.string().required("Required"),
                address: yup.string().required("Required"),
                city: yup.string().required("Required"),
                state: yup.string().required("Required"),
                zip: yup.string().required("Required"),
            })
        ).min(1, "Please add an entry"),
    }),

    addingOwners: yup.string().when("changeOwner", { is: "yes", then: yup.string().required("Required"), }),
    addedOwners: yup.array().when(["changeOwner", "addingOwners"], {
        is: (a: string, b: string) => {
            return a === "yes" && b === "yes";
        },
        then: yup.array().of(
            yup.object().shape({
                ownerType: yup.string().required("Required"),
                firstName: yup.string().when("ownerType", { is: "person", then: yup.string().required("Required"), }),
                lastName: yup.string().when("ownerType", { is: "person", then: yup.string().required("Required"), }),

                businessName: yup.string().when("ownerType", { is: "business", then: yup.string().required("Required"), }),
                feiEin: yup.string().when("feiEinStatus", {
                    is: "listed",
                    then: yup.string().required("Please enter a valid FEI/EIN (9 digits)").length(10, "Must be 9 digits").matches(/[0-9-]{10}/g, "Must be only digits"),
                }),
                feiEinStatus: yup.string().when("ownerType", { is: "business", then: yup.string().required("Required"), }),

                address: yup.string().required("Required"),
                city: yup.string().required("Required"),
                state: yup.string().required("Required"),
                zip: yup.string().required("Required"),
            })
        ).min(1, "Please add an entry"),
    }),

    updateCurrentAddress: yup.string().when("informationStillAccurate", { is: "no", then: yup.string().required("Required"), }),
    updatedCurrentAddress: yup.object().when("updateCurrentAddress", {
        is: "yes",
        then: yup.object().shape({
            address: yup.string().required("Required"),
            address2: yup.string(),
            city: yup.string().required("Required"),
            state: yup.string().required("Required"),
            zip: yup.string().required("Required"),
            county: yup.string().required("Required"),
        }),
    }),

    changeOwnerAddresses: yup.string().when("informationStillAccurate", { is: "no", then: yup.string().required("Required"), }),
    changedOwnerAddresses: yup.array().when(["informationStillAccurate", "changeOwnerAddresses"], {
        is: (a: string, b: string) => {
            return a === "no" && b === "yes";
        },
        then: yup.array().of(
            yup.object().shape({
                name: yup.string().required("Required"),
                address: yup.string().required("Required"),
                city: yup.string().required("Required"),
                state: yup.string().required("Required"),
                zip: yup.string().required("Required"),
            })
        ).min(1, "Please add an entry"),
    }),

    digitalSignature: yup.string().required("Required"),
})